import {
  Box,
  CircularProgress,
  Stack,
  SxProps,
  Tab,
  Tabs,
  Theme,
  Typography,
} from '@mui/material';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import ReportHeaderDetails from './reportHeaderDetails';
import ReportInfoDetails from './reportInfoDetails';
import SectionWrapper from './sectionWrapper';

import { ReportDetailsProp } from '../../../../../components/customer/report/types';
import { caseManagerReportFetcher } from '../../../../../services/fetchers/caseManagerReport';
import { RootState } from '../../../../../utils/store';
import AttachmentTab from '../../../reports/reportDocuments/attachments/attachments';

interface ReportInfoSectionProps {
  id: string | undefined;
}

const ReportInfoSection = ({ id }: ReportInfoSectionProps) => {
  const REPORT_URL = '/report';

  const { data, error, isLoading } = useSWR<ReportDetailsProp, AxiosError>(
    () => {
      if (id) {
        return REPORT_URL + '/' + id;
      }
      return null;
    },
    caseManagerReportFetcher,
  );

  const intl = useIntl();
  const DETAILS_TAB_VALUE = 0;
  const ATTACHMENTS_TAB_VALUE = 1;

  const [selectedTab, setTab] = useState(DETAILS_TAB_VALUE);
  const roles = useSelector((state: RootState) => state.userRoles);

  const tabStyle: SxProps<Theme> = {
    textTransform: 'none',
    '&.Mui-selected': {
      color: (theme) => theme.palette.teal.main,
    },
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  if (isLoading) {
    return (
      <SectionWrapper>
        <Stack justifyContent='center' alignItems='center' height='100%'>
          <CircularProgress />
        </Stack>
      </SectionWrapper>
    );
  }

  if (error && !isLoading) {
    return (
      <SectionWrapper>
        <Stack justifyContent='center' alignItems='center' height='100%'>
          <Typography color='#878787'>
            <FormattedMessage defaultMessage='There has been a problem fetching report details. Please try again later.' />
          </Typography>
        </Stack>
      </SectionWrapper>
    );
  }

  if (!data) {
    return (
      <SectionWrapper>
        <Stack justifyContent='center' alignItems='center' height='100%'>
          <Typography color='#878787'>
            <FormattedMessage defaultMessage='No report data found.' />
          </Typography>
        </Stack>
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper>
      <ReportHeaderDetails
        reporter={data.reporter}
        reportDate={data.createdAt}
        country={data.country.name}
      />
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        TabIndicatorProps={{
          sx: {
            backgroundColor: (theme) => theme.palette.teal.main,
            height: '3px',
          },
        }}
        sx={{
          '& .MuiTabs-scroller': {
            borderBottom: '1px solid #E7E7E7',
          },
        }}
        variant='scrollable'
      >
        <Tab
          label={intl.formatMessage({ defaultMessage: 'Details' })}
          sx={tabStyle}
        />
        <Tab
          label={intl.formatMessage({ defaultMessage: 'Attachments' })}
          sx={tabStyle}
        />
      </Tabs>
      <Box display={selectedTab === DETAILS_TAB_VALUE ? 'initial' : 'none'}>
        <ReportInfoDetails
          description={data.description}
          redactedPositions={data.reportRedactedDescriptionRanges}
        />
      </Box>
      <Box display={selectedTab === ATTACHMENTS_TAB_VALUE ? 'initial' : 'none'}>
        {data.reportAttachments.length > 0 && (
          <AttachmentTab
            attachments={data.reportAttachments}
            downloadAttachmentsFlag={roles.is_allowed_to_download_attachments}
          />
        )}
      </Box>
    </SectionWrapper>
  );
};

export default ReportInfoSection;
